import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import BaseView from '../../../js/base-view';

const OPENED_MENU_CLASS = 'is-opened';
const STICKY_CLASS = 'is-sticky';
const STICKY_BODY_CLASS = 'is-sticky-nav';
const STICKY_THRESHOLD = 80;
const TOGGLE_SELECTOR = '[data-site-navigation-toggle]';
const NAV_BAR_SELECTOR = '.site-navigation__nav-bar';
const MENU_SELECTOR = '.site-navigation__menu';
const PRESS_MEGA_SELECTOR = '.site-navigation__press-mega';
const SECONDARY_MENU_SELECTOR = '.site-navigation__secondary-wrapper';
const SECONDARY_MENU_NAV_SELECTOR = '.site-navigation__secondary-wrapper nav';
const EDITOR_INFO_SELECTOR = '.site-navigation__editor-info';
const CART_COUNT_SELECTOR = '.site-navigation__cart-count';

const TOP_MENU_SELECTOR = '.site-navigation__nav';

const MIN_FONT_SIZE_MOBILE = 24;
const MAX_FONT_SIZE_MOBILE = 28;
const MIN_FONT_SIZE_DESKTOP = 32;
const MAX_FONT_SIZE_DESKTOP = 40;

const TABLET_BREAKPOINT = 768;
const LAPTOP_BREAKPOINT = 1024;
// Aligné sur $press-nav-compact-breakpoint (SCSS) — burger / méga presse
const PRESS_NAV_COMPACT_BREAKPOINT = 1500;
const CUSTOM_DESKTOP_BREAKPOINT = 1440;

export default class SiteNavigation extends BaseView {
	/**
	 * Entrypoint
	 */
	init() {
		const isMobile = window.viewport_service.current_width < CUSTOM_DESKTOP_BREAKPOINT;

		// refs
		this.refs = {
			toggles: Array.from(this.el.querySelectorAll(TOGGLE_SELECTOR)),
			navbar: this.el.querySelector(NAV_BAR_SELECTOR),
			menu: this.el.querySelector(MENU_SELECTOR),
			pressMega: this.el.querySelector(PRESS_MEGA_SELECTOR),
			menuItems: Array.from(this.el.querySelectorAll(`${ MENU_SELECTOR } a`)),
			pressMegaItems: Array.from(this.el.querySelectorAll(`${ PRESS_MEGA_SELECTOR } a, ${ PRESS_MEGA_SELECTOR } button`)),
			secondaryMenu: this.el.querySelector(SECONDARY_MENU_SELECTOR),
			secondaryMenuNav: this.el.querySelector(SECONDARY_MENU_NAV_SELECTOR),
			editorInfo: this.el.querySelector(EDITOR_INFO_SELECTOR),
			cartCount: this.el.querySelector(CART_COUNT_SELECTOR),

			topMenu: this.el.querySelector(TOP_MENU_SELECTOR),
			topMenuItems: this.el.querySelectorAll(`${TOP_MENU_SELECTOR} > *`),
		};

		// props
		this.props = {
			secondaryMenuFontSizes: {
				min: isMobile ? MIN_FONT_SIZE_MOBILE : MIN_FONT_SIZE_DESKTOP,
				max: isMobile ? MAX_FONT_SIZE_MOBILE : MAX_FONT_SIZE_DESKTOP,
			},
		};

		// state
		this.state = {
			isNavigationOpen: false,
			isPressHeader: this.el.classList.contains('site-navigation--press-header'),
			scrollLockTarget: null,
		};

		// attach events
		this.bindEvents();

		if (this.state.isPressHeader) {
			this.initPressHeaderSticky();
			this.handlePressMegaVisibility();
			this.handleTabIndexes();
		}

		if (this.refs.secondaryMenu) {
			this.handleSecondaryMenuFontSize();
		}

		if(this.refs.topMenu)
			this.fitTopMenuWidth();
	}

	/**
	 * Bind component's events
	 */
	bindEvents() {
		this.on('resize', this.onResize.bind(this));
		this.on('click', this.onToggleButtonClick.bind(this));
		this.on('click', this.onPressMegaLinkClick.bind(this));
		this.on('keydown', this.el, this.handleKeyUp.bind(this));

		this.updateCartCount = this.updateCartCount.bind(this);
		this.onAddedToCart = this.onAddedToCart.bind(this);
		this.onRemovedFromCart = this.onRemovedFromCart.bind(this);

		this.on('added-to-cart', this.onAddedToCart, true);
		this.on('removed-from-cart', this.onRemovedFromCart, true);
		this.on('updated-cart', this.updateCartCount, true);
		this.on('cart-emptied', this.updateCartCount, true);
	}

	onAddedToCart(event) {
		this.updateCartCount();

		const productName = event && event.detail && event.detail.productName;
		this.showCartTooltip(productName || '', 'added');
	}

	onRemovedFromCart(event) {
		this.updateCartCount();

		// On n'affiche la notification que pour les retraits explicites portant un
		// nom de produit (ex. carte de parrainage). Les évènements génériques de
		// WooCommerce (page panier) ne déclenchent pas de tooltip.
		const detail = event && event.detail;
		if (detail && detail.action === 'removed') {
			this.showCartTooltip(detail.productName || '', 'removed');
		}
	}

	updateCartCount() {
		if (!window.supt || !window.supt.restAPI) return;

		fetch(`${window.supt.restAPI.root}/supt/v1/cart`)
			.then(response => response.json())
			.then((data) => {
				const cartLink = this.el.querySelector('.site-navigation__cart');
				if (!cartLink) return;

				const count = parseInt(data.item_count, 10) || 0;
				let badge = this.refs.cartCount;

				if (count > 0) {
					if (!badge) {
						badge = document.createElement('span');
						badge.className = 'site-navigation__cart-count';
						cartLink.appendChild(badge);
						this.refs.cartCount = badge;
					}
					badge.textContent = count;
				} else if (badge) {
					badge.remove();
					this.refs.cartCount = null;
				}
			});
	}

	showCartTooltip(productName, type = 'added') {
		const cartLink = this.el.querySelector('.site-navigation__cart');
		if (!cartLink) return;

		const existing = cartLink.querySelector('.site-navigation__cart-tooltip');
		if (existing) existing.remove();

		const config = (window.supt && window.supt.addToCart) || {};
		const cartUrl = config.cart_url || '#';
		const viewCartLabel = config.i18n_view_cart || 'Voir le panier';
		const closeLabel = config.i18n_close || 'Fermer';

		const tooltip = document.createElement('div');
		tooltip.className = 'site-navigation__cart-tooltip';

		const name = productName ? `<strong>${productName}</strong>` : 'Produit';
		const text = type === 'removed'
			? `${name} retiré du panier`
			: `${name} ajouté au panier`;

		tooltip.innerHTML =
			`<button type="button" class="site-navigation__cart-tooltip-close" aria-label="${closeLabel}">` +
				'<svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true" focusable="false">' +
					'<path d="M1 1l10 10M11 1L1 11" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>' +
				'</svg>' +
			'</button>' +
			`<p class="site-navigation__cart-tooltip-text">${text}</p>` +
			`<a href="${cartUrl}" class="site-navigation__cart-tooltip-btn">${viewCartLabel}</a>`;

		cartLink.appendChild(tooltip);

		const hideTooltip = () => {
			if (this._tooltipTimer) {
				clearTimeout(this._tooltipTimer);
				this._tooltipTimer = null;
			}
			tooltip.classList.remove('is-visible');
			setTimeout(() => tooltip.remove(), 300);
		};

		const closeBtn = tooltip.querySelector('.site-navigation__cart-tooltip-close');
		if (closeBtn) {
			closeBtn.addEventListener('click', (ev) => {
				ev.preventDefault();
				ev.stopPropagation();
				hideTooltip();
			});
		}

		requestAnimationFrame(() => {
			tooltip.classList.add('is-visible');
		});

		if (this._tooltipTimer) clearTimeout(this._tooltipTimer);
		this._tooltipTimer = setTimeout(hideTooltip, 8000);
	}

	// --------------------------------
	// #region Event Handlers
	// --------------------------------

	handleKeyUp(ev) {
		if (this.state.isNavigationOpen && ev.keyCode === 27) {
			this.toggleMenu();
		}
	}

	toggleMenu() {
		this.el.classList.toggle(OPENED_MENU_CLASS, ! this.state.isNavigationOpen);
		this.state.isNavigationOpen = ! this.state.isNavigationOpen;
		this.handlePressMegaVisibility();
		this.handleAriaAttributtes();
		this.handleTabIndexes();
		this.handleBodyScrollLock();

		if ( ! this.state.isNavigationOpen ) {
			this.refs.toggles.forEach((toggle) => toggle.blur());
		}

		if ( this.state.isPressHeader ) {
			this.handleStickyScroll();
		}
	}

	isPressMegaMode() {
		return this.state.isPressHeader
			&& this.refs.pressMega
			&& window.viewport_service.current_width < PRESS_NAV_COMPACT_BREAKPOINT;
	}

	handlePressMegaVisibility() {
		if ( ! this.refs.pressMega ) {
			return;
		}

		const useMega = this.isPressMegaMode();
		if ( useMega && this.state.isNavigationOpen ) {
			this.refs.pressMega.hidden = false;
		} else if ( useMega || ! this.state.isNavigationOpen ) {
			// Sur desktop presse, le méga reste masqué ; à la fermeture aussi
			this.refs.pressMega.hidden = true;
		}
	}

	initPressHeaderSticky() {
		this.handleStickyScroll = this.handleStickyScroll.bind(this);
		window.addEventListener('scroll', this.handleStickyScroll, { passive: true });
		this.handleStickyScroll();
	}

	handleStickyScroll() {
		if ( ! this.state.isPressHeader ) {
			return;
		}

		// Garder l’état sticky à l’ouverture du méga : sinon le chrome header
		// (filet rouge, hauteur) change entre menu ouvert et fermé.
		const isSticky = window.scrollY > STICKY_THRESHOLD;
		this.el.classList.toggle(STICKY_CLASS, isSticky);
		document.body.classList.toggle(STICKY_BODY_CLASS, isSticky);
	}

	onToggleButtonClick(ev) {
		const toggleButton = ev.target.closest(TOGGLE_SELECTOR);
		if (! toggleButton) {
			return;
		}

		ev.preventDefault();
		this.toggleMenu();
	}

	onPressMegaLinkClick(ev) {
		if ( ! this.isPressMegaMode() || ! this.state.isNavigationOpen ) {
			return;
		}

		const link = ev.target.closest(`${PRESS_MEGA_SELECTOR} a`);
		if ( ! link ) {
			return;
		}

		// Laisser les boutons utilitaires (modales) ouvrir leur dialog
		this.toggleMenu();
	}

	handleAriaAttributtes() {
		this.refs.toggles.forEach((toggle) => {
			toggle.setAttribute('aria-expanded', this.state.isNavigationOpen);
		});
	}

	handleTabIndexes() {
		const open = this.state.isNavigationOpen;

		for (let index = 0; index < this.refs.menuItems.length; index++) {
			this.refs.menuItems[ index ].setAttribute('tabindex', open && ! this.isPressMegaMode() ? 0 : -1);
		}

		for (let index = 0; index < this.refs.pressMegaItems.length; index++) {
			this.refs.pressMegaItems[ index ].setAttribute('tabindex', open && this.isPressMegaMode() ? 0 : -1);
		}
	}

	handleBodyScrollLock() {
		if (this.state.isNavigationOpen) {
			const scrollTarget = this.isPressMegaMode() && this.refs.pressMega
				? (this.refs.pressMega.querySelector('.site-navigation__press-mega-body') || this.refs.pressMega)
				: this.el;

			this.state.scrollLockTarget = scrollTarget;
			disableBodyScroll(scrollTarget);
		}
		else {
			enableBodyScroll(this.state.scrollLockTarget || this.el);
			this.state.scrollLockTarget = null;
		}
	}

	setupSecondaryMenuMinFontSize() {
		const isMobile = window.viewport_service.current_width < CUSTOM_DESKTOP_BREAKPOINT;

		this.props.secondaryMenuFontSizes = {
			min: isMobile ? MIN_FONT_SIZE_MOBILE : MIN_FONT_SIZE_DESKTOP,
			max: isMobile ? MAX_FONT_SIZE_MOBILE : MAX_FONT_SIZE_DESKTOP,
		};
	}

	resetSecondaryMenuFontSize() {
		const isMobile = window.viewport_service.current_width < CUSTOM_DESKTOP_BREAKPOINT;
		this.refs.secondaryMenu.style.fontSize = isMobile ? `${ MAX_FONT_SIZE_MOBILE }px` : `${ MAX_FONT_SIZE_DESKTOP }px`;
	}

	menuFitsViewport() {
		const {
			navbar, secondaryMenu, secondaryMenuNav, editorInfo,
		} = this.refs;
		const isMobile = window.viewport_service.current_width < TABLET_BREAKPOINT;
		const targetRect = isMobile ? secondaryMenu.getBoundingClientRect() :
			secondaryMenuNav.getBoundingClientRect();
		const valueToCheck = isMobile ? targetRect.height : targetRect.bottom;
		let aditionalSums = 0;

		if (isMobile) {
			aditionalSums += navbar.getBoundingClientRect().height;
		}
		else {
			aditionalSums += editorInfo.getBoundingClientRect().height;
		}

		return valueToCheck <= (window.innerHeight - aditionalSums);
	}

	handleSecondaryMenuFontSize() {
		const { secondaryMenu } = this.refs;
		const { secondaryMenuFontSizes } = this.props;

		/**
		 *  💀 Let's play safe to not get stuck in the loop and crash the browser
		 * 10 covers the amount the font size needs to shrink or grow
		 */
		const maxIterations = 10;
		let iterationCount = 0;
		let currentFontSize = parseInt(window.getComputedStyle(secondaryMenu).fontSize, 10);

		while (currentFontSize > secondaryMenuFontSizes.min &&
				iterationCount < maxIterations &&
				! this.menuFitsViewport()
		) {
			currentFontSize -= 1;
			secondaryMenu.style.fontSize = `${ currentFontSize }px`;
			iterationCount += 1;
		}
	}

	fitTopMenuWidth() {
		const menuWidth = this.refs.topMenu.offsetWidth;
		let menuItemsWidth = [...this.refs.topMenuItems].reduce((totalWidth, item) => {
			return totalWidth + item.offsetWidth;
		}, 0);

		if(menuWidth > 0) {
			for (let i = this.refs.topMenuItems.length - 2; i >= 0; i--) { // loop between items except last one
				const element = this.refs.topMenuItems[i];

				if((menuItemsWidth + 10) > menuWidth) {
					menuItemsWidth -= element.offsetWidth;
					element.style.display = 'none';
				}
			}
		}
	}

	// /**
	//  * When the viewport size changes
	//  */
	onResize() {
		if (this.refs.secondaryMenu) {
			this.setupSecondaryMenuMinFontSize();
			this.resetSecondaryMenuFontSize();
			this.handleSecondaryMenuFontSize();
		}

		if(this.refs.topMenu)
			this.fitTopMenuWidth();

		if ( this.state.isPressHeader ) {
			this.handleStickyScroll();
			// Fermer le méga si on passe en desktop
			if ( this.state.isNavigationOpen && ! this.isPressMegaMode() ) {
				this.toggleMenu();
			} else {
				this.handlePressMegaVisibility();
			}
		}
	}

	// --------------------------------
	// #endregion
	// --------------------------------

	/**
	 * Before the component gets destroyed
	 * - unbind any event not bound with this.on()
	 * - reset UI if needed (any classes/attributes added?)
	 */
	beforeDestroy() {
		if ( this.handleStickyScroll ) {
			window.removeEventListener('scroll', this.handleStickyScroll);
		}
	}
}
